import React, { useState } from 'react';
import './App.css';

const faqs = [
  {
    q: "How do I create an account?",
    a: "Download the Aro Wellness app from the App Store or Google Play Store. Open the app and tap 'Sign Up'. Fill in your details including name, email, and phone number, then follow the verification steps to complete your registration."
  },
  {
    q: "How do I book a session?",
    a: "After logging in, browse available wellness services from the home screen. Select your preferred service, choose a date and time slot, then confirm your booking. You'll receive a confirmation notification once your appointment is scheduled."
  },
  {
    q: "How do I manage my subscription?",
    a: "Go to your Profile tab, then tap 'My Subscription'. From there you can view your current plan, upgrade or downgrade your subscription, and manage your payment methods."
  },
  {
    q: "What is the cancellation policy?",
    a: "Sessions can be cancelled up to 24 hours before the scheduled time for a full refund. Cancellations made less than 24 hours before will be subject to a cancellation fee. You can cancel directly from the 'My Bookings' section in the app."
  },
  {
    q: "How do I reset my password?",
    a: "On the login screen, tap 'Forgot Password'. Enter the email address associated with your account. You'll receive a password reset link via email. Follow the link to set a new password."
  },
  {
    q: "How do I contact my wellness practitioner?",
    a: "After booking a session, you can message your practitioner directly through the in-app chat feature. Go to 'My Bookings', select the relevant appointment, and tap 'Chat with Practitioner'."
  },
  {
    q: "Is my personal data secure?",
    a: "Yes. Aro Wellness uses industry-standard encryption to protect your personal information. We comply with applicable data protection regulations and never share your data with third parties without your explicit consent. For more details, please review our Privacy Policy."
  },
  {
    q: "How do I request a refund?",
    a: "If you're eligible for a refund based on our cancellation policy, you can request one through the app by going to 'My Bookings' > select the booking > 'Request Refund'. Alternatively, contact our support team directly for assistance."
  }
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <svg className={`faq-icon ${open ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="faq-answer">
        <p>{a}</p>
      </div>
    </div>
  );
}

function App() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="app">
      {/* Decorative background */}
      <div className="bg-gradient" />
      <div className="bg-circle c1" />
      <div className="bg-circle c2" />

      {/* Header */}
      <header className="header">
        <div className="container header-inner">
          <div className="logo">
            <div className="logo-mark">A</div>
            <span className="logo-text">Aro Wellness</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <p className="hero-label">Support Center</p>
          <h1 className="hero-title">How can we<br/><em>help you?</em></h1>
          <p className="hero-desc">
            We're here to ensure your wellness journey is seamless. 
            Find answers below or reach out to our team directly.
          </p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="contact-cards">
        <div className="container">
          <div className="cards-grid">
            <a href="mailto:support@arowellness.web.id" className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M2 7L12 13L22 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Email Us</h3>
              <p>support@arowellness.web.id</p>
            </a>
            <a href="https://wa.me/6282298111947" className="card" target="_blank" rel="noopener noreferrer">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 11.5C21 16.75 16.75 21 11.5 21C9.8 21 8.2 20.55 6.8 19.75L3 21L4.25 17.2C3.45 15.8 3 14.2 3 12.5C3 7.25 7.25 3 12.5 3C17.75 3 21 7.25 21 11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 10.5C9 10.5 10 12.5 12 14.5C14 12.5 15 10.5 15 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>WhatsApp</h3>
              <p>Chat with our team</p>
            </a>
            <div className="card">
              <div className="card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Hours</h3>
              <p>Mon – Fri, 09:00 – 18:00 WIB</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <p className="section-label">Common Questions</p>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((item, i) => (
              <FAQItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="form-section">
        <div className="container">
          <p className="section-label">Still need help?</p>
          <h2 className="section-title">Send us a message</h2>
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={e => setFormData({...formData, subject: e.target.value})}
                placeholder="What do you need help with?"
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                placeholder="Describe your issue in detail..."
              />
            </div>
            <button type="submit" className="submit-btn">
              {submitted ? '✓ Message Sent' : 'Send Message'}
            </button>
            {submitted && <p className="success-msg">Thank you! We'll get back to you within 24 hours.</p>}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <div className="logo">
              <div className="logo-mark sm">A</div>
              <span className="logo-text">Aro Wellness</span>
            </div>
            <p className="footer-desc">Your journey to holistic wellness starts here.</p>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <h4>Company</h4>
              <a href="https://arowellness.web.id">Website</a>
              <a href="https://arowellness.web.id/privacy">Privacy Policy</a>
              <a href="https://arowellness.web.id/terms">Terms of Service</a>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <a href="mailto:support@arowellness.web.id">Email Support</a>
              <a href="https://wa.me/6282298111947">WhatsApp</a>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Aro Wellness. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
